import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { Modal, type ModalBaseProps } from "@/components/Modal/Modal";
import { Box } from "@/design-system/Box";
import { Inline } from "@/design-system/Inline";
import { Spinner } from "@/design-system/Spinner";
import { Stack } from "@/design-system/Stack";
import { useOnIntersection } from "@/hooks/useOnIntersection";
import { useCommunityFeedQueries } from "@/queries/community-feed/queryOptions";
import { useTranslation } from "@/translations";

import { ReactionListItem } from "./ReactionListItem";

type CommentReactionListModalProps = ModalBaseProps & {
  messageId: string;
  commentId?: string;
  /** Likes gotten from already available data, so we don't show 0 likes during loading */
  initialLikes?: number;
};

export function CommentReactionListModal({
  isOpened,
  onOpenChange,
  messageId,
  commentId,
  initialLikes,
}: CommentReactionListModalProps): React.ReactNode {
  const { t } = useTranslation();

  const communityFeedQueries = useCommunityFeedQueries();
  const {
    data: commentReactionsData,
    isFetching: isFetchingCommentReactions,
    hasNextPage: hasMoreCommentReactions,
    isFetchingNextPage: isFetchingMoreCommentReactions,
    fetchNextPage: fetchMoreCommentReactions,
    error: commentReactionsError,
  } = useInfiniteQuery({
    ...communityFeedQueries.commentReactionsInfinite({
      messageId,
      commentId: commentId!,
    }),
    enabled: Boolean(commentId),
  });

  const commentReactions = useMemo(
    () => commentReactionsData?.pages.flatMap((x) => x.items ?? []) ?? [],
    [commentReactionsData],
  );
  const amountCommentReactions = commentReactionsData?.pages[0].total ?? initialLikes ?? 0;

  const loaderRef = useOnIntersection({
    threshold: 0,
    onIntersect: useCallback(() => {
      if (!isFetchingMoreCommentReactions && hasMoreCommentReactions) {
        void fetchMoreCommentReactions();
      }
    }, [fetchMoreCommentReactions, hasMoreCommentReactions, isFetchingMoreCommentReactions]),
  });

  return (
    <Modal.Root
      size="sm"
      title={t("component.community-post.comment.likes.modal.title")}
      description={t("component.community-post.comment.likes.modal.description-total", {
        count: amountCommentReactions,
      })}
      data-testid="comment-reactions-modal"
      {...{ isOpened, onOpenChange }}
    >
      <Modal.Body>
        <Stack gap="md" data-testid="comment-likes-modal-content">
          {isFetchingCommentReactions && (
            <Box padding={{ y: "md" }}>
              <Inline justify="center">
                <Spinner size="md" />
              </Inline>
            </Box>
          )}
          {!isFetchingCommentReactions &&
            (commentReactionsError || !commentReactions.length || commentReactions.length === 0) && (
              <span className="text-caption">{t("component.community-post.comment.likes.modal.no-data")}</span>
            )}
          {!isFetchingCommentReactions && (
            <div className="max-h-scroll-area-compact overflow-y-auto">
              <Stack gap="md" as="ul">
                {commentReactions.map((commentReaction) => (
                  <ReactionListItem key={commentReaction.author.id} reaction={commentReaction} />
                ))}
              </Stack>
              {hasMoreCommentReactions && (
                <div className="p-space-md" ref={loaderRef}>
                  <Box padding={{ y: "md" }}>
                    <Inline justify="center">
                      <Spinner size="md" />
                    </Inline>
                  </Box>
                </div>
              )}
            </div>
          )}
        </Stack>
      </Modal.Body>
    </Modal.Root>
  );
}
